// @flow
import React, { useState } from 'react';
import classNames from 'classnames';
import injectSheet from 'react-jss';
import isVideo from 'shared/lib/isVideo';
import EditButton from './EditButton';

type Props = {
  onDragStart: Function,
  onDragEnter: Function,
  onDragOver: Function,
  onDrop: Function,
  onDragEnd: Function,
  canMerge: boolean,
  image: Object,
  style: Object,
  isMerging: boolean,
  handleMergeStart: Function,
  handleUnmerge: Function,
  handleRemoveImage: Function,
  handleUpdateAsset: Function,
  handleAddAsset: Function,
  handleMergeComplete: Function,
  classes: Object
}

const EditableAsset = (props: Props) => {
  const {
    onDragStart,
    onDragEnter,
    onDragOver,
    onDrop,
    onDragEnd,
    canMerge,
    image,
    style,
    isMerging,
    handleMergeStart,
    handleUnmerge,
    handleRemoveImage,
    handleUpdateAsset,
    handleAddAsset,
    handleMergeComplete,
    classes: c
  } = props;

  const [onHover, setOnHover] = useState(false);

  const mergeButtons =
    image.size === 'SMALL' ? (
      <EditButton
        onClick={handleMergeStart}
        icon="border_clear"
        label="Merge"
      />
    ) : (
      <EditButton
        onClick={handleUnmerge}
        icon="gavel"
        label="Unmerge"
      />
    );

  const editAssetButtons =
    image.url ? (
      <React.Fragment>
        <EditButton
          onClick={handleRemoveImage}
          icon="remove"
          label="Remove Asset"
        />
        <EditButton
          onClick={handleUpdateAsset}
          icon="edit"
          label="Update Asset"
        />
      </React.Fragment>
    ) : (
      <EditButton
        onClick={handleAddAsset}
        icon="add"
        label="Add Asset"
      />
    );

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={classNames(c.gridUnit, canMerge && c.mergable)}
      style={style}
      onMouseEnter={() => setOnHover(true)}
      onMouseLeave={() => setOnHover(false)}
    >
      <span
        className={c.title}
        style={{ color: image.titleColor === 'Light' ? 'white' : 'black' }}
      >
        {image.title}
      </span>
      {
        isVideo(image.url) &&
        <Video classes={c} image={image} />
      }
      {
        onHover &&
          <div className={classNames(c.controls, !isMerging && c.bgColor)}>
            {
              !isMerging &&
                <React.Fragment>
                  { mergeButtons }
                  { editAssetButtons }
                </React.Fragment>
            }
            {
              isMerging && canMerge &&
                <EditButton
                  onClick={handleMergeComplete}
                  icon="border_clear"
                  label="Finish Merging"
                />
            }
          </div>
      }
    </div>
  );
};

/*
 * Ensures the video element is replaced when url/poster is updated.
 */
const Video = ({ classes: c, image }) => {
  const key = JSON.stringify({ url: image.url, poster: image.coverUrl });

  return (
    <video
      key={key}
      className={c.video}
      playsInline
      src={image.url}
      poster={image.coverUrl}
    />
  );
};

const styles = {
  gridUnit: {
    border: '1px solid rgba(247, 247, 247, .75)',
    position: 'absolute',
    cursor: 'pointer',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
    backgroundColor: '#bbb',
    overflow: 'hidden'
  },
  mergable: {
    backgroundColor: 'hsl(160, 30%, 50%) !important',
    transition: 'all .25s ease',
    '&:hover': {
      backgroundColor: 'hsl(160, 30%, 50%) !important',
      boxShadow: 'inset 2px 2px 125px -22px rgba(0,0,0,.5)'
    }
  },
  controls: {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white'
  },
  bgColor: {
    backgroundColor: 'rgba(247, 247, 247, .4)'
  },
  video: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    minWidth: '100%',
    minHeight: '100%',
    width: 'auto',
    height: 'auto',
    overflow: 'hidden'
  },
  title: {
    position: 'absolute',
    zIndex: '1',
    left: '13px',
    bottom: '13px',
    fontSize: '13px',
  },
};

export default injectSheet(styles)(EditableAsset);
